import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';

const AdminTrendBriefing: React.FC = () => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API.getTrendBriefing(), { headers: authHeaders() });
      setContent(response.data.content || '');
      setSavedContent(response.data.content || '');
      setUpdatedAt(response.data.updated_at || null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load the trend briefing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) fetchData();
  }, [fetchData, user]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.put(API.updateTrendBriefing(), { content }, { headers: authHeaders() });
      setSavedContent(response.data.content || '');
      setUpdatedAt(response.data.updated_at || null);
      setMessage('Trend briefing saved — the AI concierge will use it on the next reply.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save the trend briefing.');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = content !== savedContent;

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '16px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Trend Briefing</h1>
        <button className="btn btn-outline" onClick={fetchData} disabled={loading}>Refresh</button>
      </div>

      <p style={{ fontSize: '13px', color: '#64748B', marginTop: 0, marginBottom: '24px' }}>
        This text is added to the AI Sourcing Concierge's instructions (Telegram and the
        website chat widget) so it can speak to the current season's trends and buying
        priorities. Replace it each quarter — it always overwrites in place, there's no
        history to page through.
        {updatedAt && (
          <> Last updated {new Date(updatedAt).toLocaleString()}.</>
        )}
      </p>

      {(error || message) && (
        <div style={{
          marginBottom: '16px', padding: '16px', borderRadius: '8px', fontSize: '14px',
          background: error ? '#FEF2F2' : '#F0FDF4',
          color: error ? '#991B1B' : '#166534',
          border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
        }}>
          {error || message}
        </div>
      )}

      <div className="section-card" style={{ padding: '20px' }}>
        <textarea
          className="form-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Paste this season's trend & buying advisory here..."
          disabled={loading}
          style={{ width: '100%', minHeight: '420px', fontFamily: 'var(--sans-font, sans-serif)', fontSize: '13px', lineHeight: 1.6, resize: 'vertical' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loading || !hasUnsavedChanges}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTrendBriefing;
